import React, { useCallback, useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { emailService, labelService, apiError } from '../services/api';
import { X, Archive, Trash, Mail, BellOff, Shield, Paperclip, Star, Download, ChevronLeft, ChevronRight, Check, Sparkles, Tag, Alert } from '../ui/icons';
import Spinner from '../ui/Spinner';
import { ErrorState } from '../ui/primitives';
import { isSure } from './inbox/planGroups';
import { actionMeta } from '../ui/actions';
import { useToast } from '../ui/Toast';
import SnoozeButton from '../ui/SnoozeMenu';
import { cn } from '../ui/cn';
import { toneFor } from '../ui/avatar';
import { useHotel } from '../ui/hotel/HotelTheme';
import { hotelTone, initials } from './hotel/avatar';
import { proposalText } from './hotel/InboxBits';

function extractEmail(from) {
  const match = from?.match(/<(.+)>/);
  return match ? match[1] : from;
}
function extractName(from) {
  const match = from?.match(/^(.+?)\s*</);
  return match ? match[1].replace(/"/g, '').trim() : from;
}

// Gmail's own labels are noise in a reader: the user knows the message is in
// their inbox and whether they have read it.
const SYSTEM_LABELS = new Set([
  'INBOX', 'UNREAD', 'STARRED', 'IMPORTANT', 'SENT', 'DRAFT', 'SPAM', 'TRASH', 'CHAT',
]);

const prettyLabel = (id, byId) => {
  const known = byId?.[id];
  if (known) return known;
  if (id.startsWith('CATEGORY_')) {
    const c = id.slice('CATEGORY_'.length).toLowerCase();
    return { personal: 'Personnel', social: 'Réseaux', promotions: 'Promotions', updates: 'Mises à jour', forums: 'Forums' }[c] || c;
  }
  return id.replace(/^Label_/, '');
};

const formatBytes = (n) => {
  if (!n || n < 1024) return `${n || 0} o`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
};

// Sanitising is not optional here: the body is attacker-controlled HTML. On top
// of DOMPurify's default stripping, every surviving link is forced to open in a
// new tab with rel="noopener" so a marketing email can never navigate the app
// away from itself or reach back through window.opener.
function sanitize(html) {
  const clean = DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['style', 'form', 'input', 'button', 'iframe', 'object', 'embed'],
    FORBID_ATTR: ['srcset', 'formaction', 'ping'],
  });
  const doc = new DOMParser().parseFromString(`<div>${clean}</div>`, 'text/html');
  doc.querySelectorAll('a[href]').forEach((a) => {
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer nofollow');
  });
  // Remote images are the classic read-receipt beacon. They still load (blocking
  // them silently breaks most newsletters) but never get to carry credentials.
  doc.querySelectorAll('img').forEach((img) => {
    img.setAttribute('loading', 'lazy');
    img.setAttribute('referrerpolicy', 'no-referrer');
  });
  return doc.body.firstChild?.innerHTML || '';
}

function EmailReader({
  email,
  onClose,
  onArchive,
  onDelete,
  onUnsubscribe,
  onSnooze,
  onProtect,
  onFlag,
  unsubscribing,
  onRead,
  triage,
  aiSuggestion,
  onAiAnalyze,
  aiAnalyzing,
  onApplySuggestion,
  onRejectSuggestion,
}) {
  const toast = useToast();
  const hotel = useHotel();
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [downloading, setDownloading] = useState(null);
  const [labelsById, setLabelsById] = useState(null);
  const loadSeqRef = useRef(0);
  // Held in a ref so loadMessage can stay a stable callback.
  const onReadRef = useRef(onRead);
  onReadRef.current = onRead;
  const closeRef = useRef(null);
  const messageId = email?.messageId;

  // The list endpoint deliberately ships no bodies, so the reader fetches the
  // message it is about to show. Before this existed the panel had nothing to
  // render and every email read "Contenu complet indisponible".
  //
  // One implementation, shared by the initial load and the retry button. The
  // retry used to be a second, inline copy with no cancellation guard, so a
  // slow response could paint the body of a message the panel had already
  // moved off, and it never told the list the mail had been read.
  const loadMessage = useCallback(
    (id) => {
      if (!id) return;
      const run = ++loadSeqRef.current;
      const stale = () => run !== loadSeqRef.current;
      setFull(null);
      setLoadError('');
      setLoading(true);
      emailService
        .getEmail(id, { markRead: true })
        .then(({ data }) => {
          if (stale()) return;
          setFull(data);
          // Opening an email is what marks it read; tell the list so the unread
          // dot disappears without a full refetch.
          if (data?.isRead) onReadRef.current?.(id);
        })
        .catch((err) => {
          if (stale()) return;
          setLoadError(apiError(err, "Impossible de charger cet email."));
        })
        .finally(() => {
          if (!stale()) setLoading(false);
        });
    },
    // onRead is read through a ref: depending on it would rebuild this callback
    // on every parent render, refetching the message and re-marking it read.
    []
  );

  useEffect(() => {
    loadMessage(messageId);
    // Any response still in flight belongs to a message we are leaving.
    return () => {
      loadSeqRef.current += 1;
    };
  }, [messageId, loadMessage]);

  // Label names are per-account and rarely change; fetch once for the session.
  useEffect(() => {
    let cancelled = false;
    labelService
      .list()
      .then(({ data }) => {
        if (cancelled) return;
        const map = {};
        (data || []).forEach((l) => {
          if (l?.id && l?.name) map[l.id] = l.name;
        });
        setLabelsById(map);
      })
      .catch(() => setLabelsById({}));
    return () => {
      cancelled = true;
    };
  }, []);

  if (!email) return null;

  const merged = { ...email, ...(full || {}) };
  const canUnsubscribe = Boolean(merged.unsubUrl || merged.unsubMailto);
  const attachments = full?.attachments || [];
  // The list keeps a local isStarred flag (patched optimistically), while the
  // freshly loaded message only carries Gmail's labels. Read both so the toggle
  // reflects whichever one is newer.
  const starred = merged.isStarred ?? (merged.labelIds || []).includes('STARRED');

  // The download goes through axios because the route is session-authenticated:
  // a plain <a href> would send no Authorization header and get a 401. So the
  // bytes arrive as a Blob and we hand the browser an object URL, revoked right
  // after the click so the blob does not stay pinned in memory for the session.
  const handleDownload = async (attachment) => {
    if (!attachment?.attachmentId || !messageId) return;
    setDownloading(attachment.attachmentId);
    try {
      const { data } = await emailService.downloadAttachment(messageId, attachment.attachmentId);
      const objectUrl = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = attachment.filename || 'piece-jointe';
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Revoking in the same tick cancels the download on some browsers, which
      // read the blob after the click returns. A delay keeps it alive long
      // enough without leaking the buffer for the rest of the session.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (err) {
      // The error body is a Blob here (responseType), so the shared reader would
      // find no message: say plainly that the download failed instead.
      toast.error(apiError(err, 'Téléchargement impossible.'));
    } finally {
      setDownloading(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const name = extractName(merged.from) || 'Expéditeur inconnu';
  const html = full?.bodyHtml ? sanitize(full.bodyHtml) : null;
  const text = full?.body || '';
  const visibleLabels = (merged.labelIds || []).filter((l) => !SYSTEM_LABELS.has(l));

  return (
    <aside
      className="flex h-full w-full flex-col overflow-hidden bg-surface"
      aria-label={`Email : ${merged.subject || 'sans sujet'}`}
    >
      {triage && (
        <div className="flex flex-col gap-1.5 border-b border-hairline bg-brand-50/60 px-3 py-2 sm:px-5">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="flex h-5 w-5 items-center justify-center rounded bg-brand-600 text-white font-bold text-[10px]">
                <Sparkles size={11} />
              </span>
              <span className="text-xs font-bold text-brand-900">Tri pas-à-pas</span>
              <span className="rounded-full bg-brand-100 px-2 py-0.5 text-xs font-semibold text-brand-800">
                {triage.current} / {triage.total}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={triage.onPrev}
                disabled={!triage.hasPrev}
                className="btn-ghost btn-xs flex items-center gap-1 disabled:opacity-30"
                title="Email précédent (Flèche gauche ou K)"
              >
                <ChevronLeft size={13} />
                <span className="hidden sm:inline">Précédent</span>
              </button>
              {triage.onKeep && (
                <button
                  type="button"
                  onClick={triage.onKeep}
                  className="btn-secondary btn-xs flex items-center gap-1 border-brand-200 text-brand-700 hover:bg-brand-100"
                  title="Conserver dans la boîte et marquer comme lu (C)"
                >
                  <Check size={13} />
                  <span>Conserver</span>
                </button>
              )}
              <button
                type="button"
                onClick={triage.onNext}
                disabled={!triage.hasNext}
                className="btn-ghost btn-xs flex items-center gap-1 disabled:opacity-30"
                title="Passer a l'email suivant sans modifier (Flèche droite ou J)"
              >
                <span className="hidden sm:inline">Passer</span>
                <ChevronRight size={13} />
              </button>
            </div>
          </div>
          <div className="h-1 w-full overflow-hidden rounded-full bg-brand-100">
            <div
              className="h-full bg-brand-600 transition-all duration-300 ease-out"
              style={{ width: `${Math.max(6, Math.round((triage.current / triage.total) * 100))}%` }}
            />
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 border-b border-hairline px-3 py-2 sm:px-5 sm:py-3">
        <button
          ref={closeRef}
          onClick={onClose}
          className="btn-ghost btn-sm btn-icon"
          aria-label={triage ? 'Quitter le tri pas-a-pas' : 'Fermer le lecteur'}
          title={triage ? 'Quitter le tri pas-a-pas (Échap)' : 'Fermer le lecteur (Échap)'}
        >
          <X size={18} />
        </button>
        <div className="flex items-center gap-1">
          {onAiAnalyze && (
            <button
              type="button"
              onClick={() => onAiAnalyze(merged)}
              disabled={aiAnalyzing}
              className={cn(
                'btn-ghost btn-sm',
                triage
                  ? 'flex items-center gap-1.5 px-2.5 font-medium text-brand-700 hover:bg-brand-50'
                  : 'btn-icon text-brand-600 hover:bg-brand-50 hover:text-brand-700',
                aiSuggestion && 'bg-brand-50 text-brand-700 ring-1 ring-brand-200'
              )}
              aria-label="Demander un tri IA"
              title={aiSuggestion ? 'Recommandation IA disponible' : 'Demander un tri IA (I)'}
            >
              {aiAnalyzing ? (
                <Spinner size={18} className="text-brand-600" />
              ) : (
                <Sparkles
                  size={18}
                  className={cn(
                    aiSuggestion ? 'fill-brand-600 text-brand-600' : 'fill-brand-100 text-brand-600'
                  )}
                />
              )}
              {triage && <span className="hidden sm:inline">Tri IA</span>}
            </button>
          )}
          {onSnooze && <SnoozeButton onSnooze={onSnooze} ariaLabel="Reporter cet email" />}
          {triage?.onLabel && (
            <button
              onClick={triage.onLabel}
              className="btn-ghost btn-sm btn-icon"
              aria-label="Étiqueter cet email"
              title="Étiqueter cet email (L)"
            >
              <Tag size={18} />
            </button>
          )}
          {onFlag && (
            <>
              {/* Favori and "unread again" are the two things a reader is for
                  besides getting rid of mail: flagging what matters and putting
                  back what you opened by mistake. Both were reachable only from
                  the list's keyboard shortcuts, i.e. not while reading. */}
              <button
                onClick={() => onFlag(starred ? 'unstar' : 'star')}
                className={cn(
                  'btn-ghost btn-sm btn-icon',
                  starred ? 'text-caution-600 hover:bg-caution-50' : 'hover:bg-ink-100'
                )}
                aria-pressed={starred}
                aria-label={starred ? 'Retirer des favoris' : 'Mettre en favori'}
                title={starred ? 'Retirer des favoris' : 'Mettre en favori'}
              >
                <Star size={18} className={starred ? 'fill-current' : undefined} />
              </button>
              <button
                onClick={() => onFlag('unread')}
                className="btn-ghost btn-sm btn-icon"
                aria-label="Marquer comme non lu"
                title="Marquer comme non lu (le remettre dans la pile)"
              >
                <Mail size={18} />
              </button>
            </>
          )}
          {onProtect && (
            <button
              onClick={onProtect}
              className="btn-ghost btn-sm btn-icon text-positive-600 hover:bg-positive-50"
              aria-label="Protéger cet expéditeur"
              title="Protéger cet expéditeur (jamais archivé/supprimé automatiquement)"
            >
              <Shield size={18} />
            </button>
          )}
          <button
            onClick={onArchive}
            className={cn(
              'btn-ghost btn-sm',
              triage ? 'flex items-center gap-1 px-2.5 font-medium text-ink-800 hover:bg-brand-50 hover:text-brand-700' : 'btn-icon'
            )}
            aria-label="Archiver"
            title="Archiver (E)"
          >
            <Archive size={18} />
            {triage && <span className="hidden sm:inline">Archiver</span>}
          </button>
          <button
            onClick={onDelete}
            className={cn(
              'btn-ghost btn-sm text-danger-600 hover:bg-danger-50',
              triage ? 'flex items-center gap-1 px-2.5 font-medium' : 'btn-icon'
            )}
            aria-label="Supprimer"
            title="Supprimer (Suppr)"
          >
            <Trash size={18} />
            {triage && <span className="hidden sm:inline">Supprimer</span>}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6 sm:py-6">
        <h2 className={hotel ? 'hd-reader-title' : 'text-lg font-bold leading-snug text-ink-900 sm:text-xl'}>
          {merged.subject || '(Sans sujet)'}
        </h2>

        <div className="mt-4 flex items-center gap-3">
          <span
            className={cn(
              'flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-base font-bold text-white',
              hotel ? hotelTone(merged.from) : toneFor(merged.from)
            )}
            aria-hidden
          >
            {hotel ? initials(name) : name[0]?.toUpperCase() || '?'}
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate font-semibold text-ink-900">{name}</div>
            <div className="truncate text-sm text-muted">{extractEmail(merged.from)}</div>
          </div>
          {merged.receivedDate && (
            <time
              dateTime={merged.receivedDate}
              className="hidden shrink-0 text-right text-xs text-muted sm:block"
            >
              {formatDate(merged.receivedDate)}
            </time>
          )}
        </div>

        {/* The hotel's proposal: the same verdict as the triage plan, set as
            what Mailsorter would do, the reason under it, "À vérifier" at the
            same threshold, and the same two answers (Ignorer, the verb). */}
        {hotel && aiAnalyzing && (
          <div className="hd-reader-prop" role="status">
            <div className="ht-prop">
              <Spinner size={22} className="shrink-0 text-brand-600" />
              <div>
                <small>Mailsorter lit cet e-mail</small>
                <b>Un instant.</b>
              </div>
            </div>
          </div>
        )}
        {hotel && !aiAnalyzing && aiSuggestion && (() => {
          const meta = actionMeta(aiSuggestion.action);
          return (
            <div className="hd-reader-prop">
              <div className={cn('ht-prop', aiSuggestion.action === 'keep' && 'is-safe')}>
                <Sparkles size={26} />
                <div className="min-w-0">
                  <small>Mailsorter propose</small>
                  <b>{proposalText(aiSuggestion)}</b>
                  {!isSure(aiSuggestion.confidence) && (
                    <span className="chip mt-1.5 bg-caution-50 py-0.5 text-caution-700">
                      <Alert size={12} aria-hidden /> À vérifier
                    </span>
                  )}
                  {aiSuggestion.reasoning && <p>{aiSuggestion.reasoning}</p>}
                </div>
              </div>
              {(onApplySuggestion || onRejectSuggestion) && (
                <div className="hd-reader-prop__acts">
                  {onApplySuggestion && (
                    <button
                      type="button"
                      onClick={() => onApplySuggestion(aiSuggestion)}
                      className="btn-primary btn-sm"
                      aria-label={`${proposalText(aiSuggestion)} : appliquer la recommandation`}
                    >
                      <meta.Icon size={14} aria-hidden /> {meta.label}
                    </button>
                  )}
                  {onRejectSuggestion && (
                    <button type="button" onClick={() => onRejectSuggestion(aiSuggestion)} className="btn-secondary btn-sm">
                      Ignorer
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* AI Analyzing banner */}
        {!hotel && aiAnalyzing && (
          <div className="mt-4 flex items-center gap-3 rounded-xl border border-brand-200/80 bg-brand-50/60 px-4 py-3 text-brand-900 animate-pulse">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-100 text-brand-600">
              <Sparkles size={18} className="animate-spin" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold text-brand-900">Analyse IA en cours...</div>
              <div className="text-xs text-brand-700/80">Recherche de la meilleure action pour cet email</div>
            </div>
          </div>
        )}

        {/* The same verdict the triage plan shows, in the same words: the verb
            written out (on the button too), the reason in full, and a flag only
            when the model is unsure. It used to carry a confidence ring, a
            generic "Appliquer", a gradient the design system rules out (its
            white middle band glared in the dark theme), and accents that had
            been saved as literal "?" bytes. */}
        {!hotel && !aiAnalyzing && aiSuggestion && (() => {
          const meta = actionMeta(aiSuggestion.action);
          const verdict =
            aiSuggestion.action === 'label' && aiSuggestion.labelName
              ? `${meta.label} « ${aiSuggestion.labelName} »`
              : meta.label;
          return (
            <div className="mt-4 rounded-xl border border-brand-200/80 bg-brand-50/60 p-4">
              <div className="flex items-start gap-3">
                <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', meta.chip)} aria-hidden>
                  <meta.Icon size={18} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-brand-700">
                      <Sparkles size={13} aria-hidden /> Recommandation IA
                    </span>
                    {!isSure(aiSuggestion.confidence) && (
                      <span className="chip bg-caution-50 py-0.5 text-caution-700">
                        <Alert size={12} aria-hidden /> À vérifier
                      </span>
                    )}
                  </div>
                  <p className="mt-1 font-semibold text-ink-900">{verdict}</p>
                  {aiSuggestion.reasoning && (
                    <p className="mt-0.5 text-sm leading-relaxed text-ink-700">{aiSuggestion.reasoning}</p>
                  )}
                </div>
              </div>
              {(onApplySuggestion || onRejectSuggestion) && (
                <div className="mt-3 flex items-center justify-end gap-1.5">
                  {onRejectSuggestion && (
                    <button
                      type="button"
                      onClick={() => onRejectSuggestion(aiSuggestion)}
                      className="btn-ghost h-9 rounded-lg px-3"
                      title="Ignorer cette recommandation"
                    >
                      Ignorer
                    </button>
                  )}
                  {onApplySuggestion && (
                    <button
                      type="button"
                      onClick={() => onApplySuggestion(aiSuggestion)}
                      className={cn('btn h-9 rounded-lg px-3', meta.quiet || 'bg-ink-100 text-ink-700 hover:bg-ink-200')}
                      aria-label={`${verdict} : appliquer la recommandation`}
                    >
                      <meta.Icon size={15} aria-hidden />
                      {meta.label}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {canUnsubscribe && onUnsubscribe && (
          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-caution-100 bg-caution-50 px-4 py-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-caution-100 text-caution-700">
              <BellOff size={18} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold text-ink-900">Vous recevez trop d'emails de cet expéditeur ?</div>
              <div className="text-xs text-muted">
                {merged.unsubOneClick
                  ? 'Désabonnement instantané, sans quitter Mailsorter.'
                  : "On ouvre la page de désabonnement pour vous."}
              </div>
            </div>
            <button
              onClick={onUnsubscribe}
              disabled={unsubscribing}
              className="btn-secondary btn-sm shrink-0 border-caution-100 text-caution-700 hover:bg-caution-100"
            >
              {unsubscribing ? <Spinner size={16} /> : <BellOff size={16} />} Se désabonner
            </button>
          </div>
        )}

        {attachments.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {attachments.map((a, i) => {
              // A part with no attachment id carries its bytes inline in the
              // payload; there is nothing to fetch, so it stays a plain chip
              // rather than a button that could only fail.
              const downloadable = Boolean(a.attachmentId);
              const label = `${a.filename} · ${a.mimeType || 'fichier'}`;
              if (!downloadable) {
                return (
                  <span key={`${a.filename}-${i}`} className="chip bg-ink-100 text-ink-700" title={label}>
                    <Paperclip size={13} />
                    <span className="max-w-[180px] truncate">{a.filename}</span>
                    <span className="text-muted">{formatBytes(a.size)}</span>
                  </span>
                );
              }
              const busy = downloading === a.attachmentId;
              return (
                <button
                  key={`${a.filename}-${i}`}
                  type="button"
                  onClick={() => handleDownload(a)}
                  disabled={busy}
                  className="chip bg-ink-100 text-ink-700 transition-colors hover:bg-brand-50 hover:text-brand-700 disabled:opacity-60"
                  title={`Télécharger ${label}`}
                >
                  {busy ? <Spinner size={13} /> : <Download size={13} />}
                  <span className="max-w-[180px] truncate">{a.filename}</span>
                  <span className="text-muted">{formatBytes(a.size)}</span>
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-6 border-t border-hairline pt-6">
          {loading ? (
            <div className="space-y-3" aria-label="Chargement du message" role="status">
              <div className="skeleton h-3 w-11/12" />
              <div className="skeleton h-3 w-full" />
              <div className="skeleton h-3 w-9/12" />
              <div className="skeleton h-3 w-10/12" />
              <div className="skeleton h-32 w-full" />
            </div>
          ) : loadError ? (
            <ErrorState
              compact
              title="Message illisible"
              message={loadError}
              onRetry={() => loadMessage(messageId)}
            />
          ) : html ? (
            <div className="email-body" dangerouslySetInnerHTML={{ __html: html }} />
          ) : text ? (
            <div className="email-body-plain">{text}</div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm leading-relaxed text-ink-700">{merged.snippet}</p>
              <div className="flex items-center gap-2 rounded-xl bg-surface-sunken px-4 py-3 text-xs text-muted">
                <Mail size={16} />
                Cet email ne contient pas de texte affichable.
              </div>
            </div>
          )}
        </div>

        {/* STARRED est une étiquette système, donc filtrée de visibleLabels :
            gardée sur la seule longueur de cette liste, la puce « Favori » ne
            s'affichait que sur un email portant par ailleurs une étiquette
            utilisateur. Mettre en favori depuis le lecteur ne montrait alors
            rien du tout. */}
        {(starred || visibleLabels.length > 0) && (
          <div className="mt-6 flex flex-wrap gap-2 border-t border-hairline pt-5">
            {starred && (
              <span className="chip bg-caution-50 text-caution-700">
                <Star size={13} /> Favori
              </span>
            )}
            {visibleLabels.map((label) => (
              <span key={label} className="chip bg-ink-100 text-ink-700">
                <span className="max-w-[200px] truncate">{prettyLabel(label, labelsById)}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}

export default EmailReader;
